import {io, Socket} from "socket.io-client";
import {BehaviorSubject} from 'rxjs';
import IWDevice from "./IWDevice";

export enum ConnectionState {
    Disconnected = "DISCONNECTED",
    Connecting = "CONNECTING",
    Connected = "CONNECTED",
    Reconnecting = "RECONNECTING",
}

class IWBrokerClient {
    requestId: number = 0;
    brokerURL: string;
    socket: Socket;
    connectionState$: BehaviorSubject<ConnectionState>;

    constructor() {
        this.brokerURL = `${window.location.protocol}//${window.location.hostname}:${window.location.protocol === 'http:' ? 8501 : window.location.port}`;

        this.connectionState$ = new BehaviorSubject<ConnectionState>(ConnectionState.Connecting);

        this.socket = io(this.brokerURL, {
            transports: ['websocket'],
            query: {
                platform: IWDevice.getPlatforms(),
                device: "ionic-app"
            },
            auth: (async (cb) => {
                cb({
                    token: await IWDevice.getDeviceToken()
                });
            })
        });

        this.socket.on('connect', () => {
            console.info(`(${this.constructor.name}) Connected.`);
            this.connectionState$.next(ConnectionState.Connected);
        });

        this.socket.on('connect_error', (error: any) => {
            console.warn(`(${this.constructor.name}) Connect Error`, error);
        });

        this.socket.on('disconnect', () => {
            console.warn(`(${this.constructor.name}) Disconnected.`);
            this.connectionState$.next(ConnectionState.Disconnected);
        });

        this.socket.io.on('reconnect_attempt', () => {
            console.info(`(${this.constructor.name}) Reconnect attempt`);
            this.connectionState$.next(ConnectionState.Reconnecting)
        });

    }

    async socketRequest(socket: any, event: string, ...args: any[]) {
        const requestId = ++this.requestId;

        return new Promise((resolve, reject) => {
            const loggableArgs = event === 'google login' ? [{idToken: '[REDACTED]'}] : args;
            console.log(`[${requestId}] -> `, event, loggableArgs);

            const disconnectHandler = () => {
                console.log(`[${requestId}] <-x- DISCONNECTED`);

                reject({
                    code: 1006,
                    message: 'Disconnected'
                });
            };

            const timeoutHandler = () => {
                console.warn(`[${requestId}] <-?- Request Timeout?`);
            };

            this.socket.once('disconnect', disconnectHandler);
            const timeout = setTimeout(timeoutHandler, 30000);

            this.socket.emit(event, ...args, (response: any) => {
                this.socket.off('disconnect', disconnectHandler);
                clearInterval(timeout);

                const loggableResponse = event === 'exterior gate pin'
                    ? {...response, pin: '[REDACTED]'}
                    : response;
                console.log(`[${requestId}] <- `, event, loggableResponse);

                if (response?.code && response.code !== 200) {
                    if (response.code >= 500) {
                        console.error(event, response);
                    } else {
                        console.warn(event, response);
                    }
                    reject(response);
                }

                resolve(response);
            });
        });
    }

    async volatileRequest(event: string, ...args: any[]) {
        return this.socketRequest(this.socket.volatile, event, ...args);
    }

    async request(event: string, ...args: any[]) {
        return this.socketRequest(this.socket, event, ...args);
    }

    async httpRequest(path: string, data: object): Promise<Response> {
        return await fetch(this.brokerURL + path, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
    }

    getCameraMjpegURL(cameraName: 'workshop-roller-door' | 'car-park') {
        return this.brokerURL + '/camera/' + encodeURIComponent(cameraName) + '/mjpeg';
    }

    getAvatarURL(userId: Number) {
        return this.brokerURL + '/avatar/' + userId;
    }

    getHVACGraphURL(groupId: Number, period: String = '1d', nonce: any) {
        return this.brokerURL + '/hvac/graph/' + groupId + '?time=' + period + '&nonce=' + nonce;
    }

    getWeatherGraphURL(period: String = '1d', nonce: any) {
        return this.brokerURL + '/weather/graph?time=' + period + '&nonce=' + nonce;
    }
}

export default new IWBrokerClient();
